/**
 * WebGL2 implementation of the GSplat GPU-leaf port.
 *
 * The ownership split follows PlayCanvas' GraphicsDevice-backed GSplat resources at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License): format/Resource/World remain shared,
 * while textures, the WebGL order texture, materials and raster objects are device-local leaves.
 */
import * as THREE from 'three'
import type { GraphicsBackend } from '@/graphics/GraphicsBackend'
import { BackendMismatchError, UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import { WebGL2Backend } from '@/graphics/WebGL2Backend'
import type { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import { gsplatChunks } from '@/plugins/gsplat/shaders/gsplat-chunks'
import type { GSplatWorkBufferRenderInfo } from '@/plugins/gsplat/unified/resource/GSplatResourceBase'
import { GSPLAT_SOURCE_FORMAT_CHUNKS } from '@/plugins/gsplat/unified/shaders/gsplatSourceFormatChunks'
import type { GSplatInfo } from '@/plugins/gsplat/unified/world/GSplatInfo'
import type {
  GSplatWorkBufferCopyInput,
  GSplatWorkBufferCopyStage,
  GSplatWorkBufferPass,
  GSplatWorkBufferPassFactory,
  GSplatWorkBufferPassMode,
} from '@/plugins/gsplat/unified/world/GSplatWorkBuffer'
import type {
  GSplatBufferDescriptor,
  GSplatBufferLeaf,
  GSplatComputePrimitive,
  GSplatComputePrimitiveDescriptor,
  GSplatDrawObjectLeaf,
  GSplatGraphicsAdapter,
  GSplatGraphicsAdapterFactory,
  GSplatGraphicsLimits,
  GSplatIndirectPrimitive,
  GSplatMaterialBlendMode,
  GSplatMaterialLeaf,
  GSplatMaterialRasterState,
  GSplatMaterialSnapshot,
  GSplatPickReadbackRegion,
  GSplatPickTargetDescriptor,
  GSplatPickTargetLease,
  GSplatProcessingPassExecution,
  GSplatProcessingPassLeaf,
  GSplatProcessingPipelineDescriptor,
  GSplatProcessingPipelineLeaf,
  GSplatProcessingTargetDescriptor,
  GSplatProcessingTargetLeaf,
  GSplatRelightingRenderRegion,
  GSplatRelightingTargetDescriptor,
  GSplatRelightingTargetLease,
  GSplatShaderLanguage,
  GSplatSubmitPrimitive,
  GSplatTextureDescriptor,
  GSplatTextureDimensions,
  GSplatTextureFormat,
  GSplatTextureFormatInfo,
  GSplatTextureLeaf,
  GSplatWorkBufferSourcePipelineDescriptor,
  GSplatWorkBufferSourcePipelineLeaf,
} from './GSplatGraphicsAdapter'
import { resolveScalarFilterMode } from './scalarFilterMode'
import { WEBGL_GSPLAT_WORK_BUFFER_CHUNKS } from './webglGSplatWorkBufferChunks'
import { readWebGLRenderTargetPixels, unbindWebGLPixelPackBuffer } from './webglReadback'

type TextureArray = Uint8Array | Uint16Array | Uint32Array | Float32Array

interface WebGLFormatMapping {
  readonly info: GSplatTextureFormatInfo
  readonly pixelFormat: THREE.PixelFormat
  readonly dataType: THREE.TextureDataType
  readonly internalFormat: THREE.PixelFormatGPU
  readonly channels: number
  createArray(length: number): TextureArray
}

const FORMAT_MAPPINGS: Readonly<Record<GSplatTextureFormat, WebGLFormatMapping>> = Object.freeze({
  r8unorm: mapping(
    'float',
    'vec4f',
    true,
    true,
    1,
    THREE.RedFormat,
    THREE.UnsignedByteType,
    'R8',
    1,
    Uint8Array
  ),
  r16uint: mapping(
    'uint',
    'vec4u',
    true,
    false,
    2,
    THREE.RedIntegerFormat,
    THREE.UnsignedShortType,
    'R16UI',
    1,
    Uint16Array
  ),
  r32uint: mapping(
    'uint',
    'vec4u',
    true,
    false,
    4,
    THREE.RedIntegerFormat,
    THREE.UnsignedIntType,
    'R32UI',
    1,
    Uint32Array
  ),
  rg16float: mapping(
    'float',
    'vec4f',
    true,
    false,
    4,
    THREE.RGFormat,
    THREE.HalfFloatType,
    'RG16F',
    2,
    Uint16Array
  ),
  rg32uint: mapping(
    'uint',
    'vec4u',
    true,
    false,
    8,
    THREE.RGIntegerFormat,
    THREE.UnsignedIntType,
    'RG32UI',
    2,
    Uint32Array
  ),
  rgba8unorm: mapping(
    'float',
    'vec4f',
    true,
    true,
    4,
    THREE.RGBAFormat,
    THREE.UnsignedByteType,
    'RGBA8',
    4,
    Uint8Array
  ),
  rgba16float: mapping(
    'float',
    'vec4f',
    true,
    false,
    8,
    THREE.RGBAFormat,
    THREE.HalfFloatType,
    'RGBA16F',
    4,
    Uint16Array
  ),
  rgba16uint: mapping(
    'uint',
    'vec4u',
    true,
    false,
    8,
    THREE.RGBAIntegerFormat,
    THREE.UnsignedShortType,
    'RGBA16UI',
    4,
    Uint16Array
  ),
  rgba32float: mapping(
    'float',
    'vec4f',
    true,
    false,
    16,
    THREE.RGBAFormat,
    THREE.FloatType,
    'RGBA32F',
    4,
    Float32Array
  ),
  rgba32uint: mapping(
    'uint',
    'vec4u',
    true,
    false,
    16,
    THREE.RGBAIntegerFormat,
    THREE.UnsignedIntType,
    'RGBA32UI',
    4,
    Uint32Array
  ),
})

function mapping(
  sampleType: GSplatTextureFormatInfo['sampleType'],
  shaderValueType: GSplatTextureFormatInfo['shaderValueType'],
  renderable: boolean,
  filterable: boolean,
  bytesPerTexel: number,
  pixelFormat: THREE.PixelFormat,
  dataType: THREE.TextureDataType,
  internalFormat: THREE.PixelFormatGPU,
  channels: number,
  ArrayType:
    | Uint8ArrayConstructor
    | Uint16ArrayConstructor
    | Uint32ArrayConstructor
    | Float32ArrayConstructor
): WebGLFormatMapping {
  return Object.freeze({
    info: Object.freeze({
      sampleType,
      shaderValueType,
      renderable,
      filterable,
      storageWritable: false,
      bytesPerTexel,
    }),
    pixelFormat,
    dataType,
    internalFormat,
    channels,
    createArray: (length: number) => new ArrayType(length),
  })
}

abstract class WebGLLeaf {
  protected disposed = false

  constructor(
    readonly owner: WebGLGSplatGraphicsAdapter,
    private readonly retire: (leaf: WebGLLeaf) => void
  ) {}

  protected finishDispose(): void {
    this.retire(this)
  }

  assertActive(): void {
    if (this.disposed) throw new Error('WebGL GSplat GPU leaf 已销毁')
    this.owner.assertActive()
  }

  abstract dispose(): void
}

class WebGLTextureLeaf extends WebGLLeaf implements GSplatTextureLeaf {
  readonly label: string
  readonly format: GSplatTextureFormat
  readonly width: number
  readonly height: number
  readonly texture: THREE.DataTexture
  cpuData: TextureArray | null
  private readonly readTargets = new Map<WebGLProcessingTargetLeaf, number>()

  constructor(
    owner: WebGLGSplatGraphicsAdapter,
    retire: (leaf: WebGLLeaf) => void,
    descriptor: GSplatTextureDescriptor,
    mapping: WebGLFormatMapping,
    data: TextureArray
  ) {
    super(owner, retire)
    const gpuGenerated =
      descriptor.usages.includes('render-attachment') && descriptor.data === undefined
    this.label = descriptor.label
    this.format = descriptor.format
    this.width = descriptor.width
    this.height = descriptor.height
    this.cpuData = gpuGenerated ? null : data
    this.texture = new THREE.DataTexture(
      gpuGenerated ? null : data,
      descriptor.width,
      descriptor.height,
      mapping.pixelFormat,
      mapping.dataType
    )
    this.texture.name = descriptor.label
    this.texture.internalFormat = mapping.internalFormat
    this.texture.minFilter = toMinFilter(descriptor.minFilter)
    this.texture.magFilter = toMagFilter(descriptor.magFilter)
    this.texture.wrapS = toWrapping(descriptor.addressU)
    this.texture.wrapT = toWrapping(descriptor.addressV)
    this.texture.generateMipmaps = false
    this.texture.flipY = false
    this.texture.unpackAlignment = 1
    // Render attachment 由 FBO 首次绑定分配；提前 needsUpdate 会让 Three 在后续采样时
    // 把初始零数组重新上传，覆盖已经写入的 MRT 内容。
    if (!gpuGenerated) this.texture.needsUpdate = true
  }

  releaseSource(): void {
    this.assertActive()
    this.owner.uploadTextureBeforeSourceRelease(this)
    this.cpuData = null
    this.texture.image.data = null
  }

  markGpuWritten(): void {
    this.assertActive()
    this.cpuData = null
    this.texture.image.data = null
    this.owner.markTextureGpuWritten(this)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    for (const target of this.readTargets.keys()) target.detach(this)
    this.readTargets.clear()
    this.cpuData = null
    this.texture.renderTarget = null
    this.texture.isRenderTargetTexture = false
    this.texture.dispose()
    this.finishDispose()
  }

  attachReadTarget(target: WebGLProcessingTargetLeaf, attachment: number): void {
    this.readTargets.set(target, attachment)
    this.texture.isRenderTargetTexture = true
    this.texture.renderTarget = target.target
  }

  detachReadTarget(target: WebGLProcessingTargetLeaf): void {
    this.readTargets.delete(target)
    const next = this.readTargets.keys().next()
    this.texture.renderTarget = next.done ? null : next.value.target
  }

  getReadTarget(): Readonly<{ target: WebGLProcessingTargetLeaf; attachment: number }> | null {
    const first = this.readTargets.entries().next()
    return first.done ? null : { target: first.value[0], attachment: first.value[1] }
  }
}

class WebGLBufferLeaf extends WebGLLeaf implements GSplatBufferLeaf {
  readonly label: string
  readonly byteLength: number
  readonly usages: ReadonlySet<GSplatBufferDescriptor['usages'][number]>
  readonly bytes: Uint8Array
  readonly storageTexture: THREE.DataTexture | null
  private readonly updateListeners = new Set<() => void>()

  constructor(
    owner: WebGLGSplatGraphicsAdapter,
    retire: (leaf: WebGLLeaf) => void,
    descriptor: GSplatBufferDescriptor
  ) {
    super(owner, retire)
    this.label = descriptor.label
    this.byteLength = descriptor.byteLength
    this.usages = new Set(descriptor.usages)
    this.bytes = new Uint8Array(descriptor.byteLength)
    if (this.usages.has('storage')) {
      const elements = Math.ceil(descriptor.byteLength / Uint32Array.BYTES_PER_ELEMENT)
      const dimensions = owner.calculateTextureDimensions(elements)
      const backing = new Uint32Array(dimensions.width * dimensions.height)
      this.storageTexture = new THREE.DataTexture(
        backing,
        dimensions.width,
        dimensions.height,
        THREE.RedIntegerFormat,
        THREE.UnsignedIntType
      )
      this.storageTexture.name = descriptor.label
      this.storageTexture.internalFormat = 'R32UI'
      this.storageTexture.minFilter = THREE.NearestFilter
      this.storageTexture.magFilter = THREE.NearestFilter
      this.storageTexture.wrapS = THREE.ClampToEdgeWrapping
      this.storageTexture.wrapT = THREE.ClampToEdgeWrapping
      this.storageTexture.generateMipmaps = false
      this.storageTexture.needsUpdate = true
    } else {
      this.storageTexture = null
    }
  }

  onUpdate(listener: () => void): void {
    this.updateListeners.add(listener)
  }

  notifyUpdated(): void {
    if (this.storageTexture) {
      const target = this.storageTexture.image.data as Uint32Array
      target.fill(0)
      target.set(
        new Uint32Array(
          this.bytes.buffer,
          this.bytes.byteOffset,
          Math.floor(this.bytes.byteLength / Uint32Array.BYTES_PER_ELEMENT)
        )
      )
      this.storageTexture.needsUpdate = true
    }
    for (const listener of this.updateListeners) listener()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.updateListeners.clear()
    this.storageTexture?.dispose()
    this.finishDispose()
  }
}

class WebGLMaterialLeaf extends WebGLLeaf implements GSplatMaterialLeaf {
  readonly language = 'glsl' as const
  readonly material: THREE.RawShaderMaterial
  revision = 0
  private readonly parameters = new Map<string, unknown>()
  private readonly defines = new Map<string, unknown>()
  private readonly chunks = new Map<string, string>()
  private rasterState: GSplatMaterialRasterState = Object.freeze({
    cullMode: 'none',
    blendMode: 'premultiplied',
    depthTest: true,
    depthWrite: false,
  })
  private workBufferOutputTypes: readonly GSplatTextureFormatInfo['shaderValueType'][] | null = null

  constructor(
    owner: WebGLGSplatGraphicsAdapter,
    retire: (leaf: WebGLLeaf) => void,
    readonly label: string,
    private readonly vertexSource: string,
    private readonly fragmentSource: string
  ) {
    super(owner, retire)
    this.material = new THREE.RawShaderMaterial({
      name: label,
      glslVersion: THREE.GLSL3,
      vertexShader: normalizeShaderSource(vertexSource, 'vertex'),
      fragmentShader: normalizeShaderSource(fragmentSource, 'fragment'),
      uniforms: {},
      transparent: true,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      premultipliedAlpha: true,
    })
  }

  setParameter(name: string, value: unknown): void {
    this.assertActive()
    this.parameters.set(name, value)
    this.material.uniforms[name] = { value: this.owner.unwrapParameter(value) }
    this.material.uniformsNeedUpdate = true
  }

  getParameter(name: string): unknown {
    return this.parameters.get(name)
  }

  deleteParameter(name: string): void {
    this.assertActive()
    this.parameters.delete(name)
    delete this.material.uniforms[name]
    this.material.uniformsNeedUpdate = true
  }

  getShaderChunks(language: GSplatShaderLanguage): Map<string, string> {
    if (language !== this.language)
      throw new Error(`WebGL material 不支持 ${language} shader chunks`)
    return this.chunks
  }

  getDefine(name: string): unknown {
    return this.defines.get(name)
  }

  setDefine(name: string, value: unknown): void {
    this.assertActive()
    if (value === undefined) this.defines.delete(name)
    else this.defines.set(name, value)
  }

  captureSnapshot(language: GSplatShaderLanguage): GSplatMaterialSnapshot {
    this.assertActive()
    if (language !== this.language) {
      throw new Error(`WebGL material 不支持 ${language} snapshot`)
    }
    return Object.freeze({
      revision: this.revision,
      parameters: Object.freeze([...this.parameters].map((entry) => Object.freeze(entry))),
      defines: Object.freeze([...this.defines].map((entry) => Object.freeze(entry))),
      shaderChunks: Object.freeze([...this.chunks].map((entry) => Object.freeze(entry))),
    })
  }

  getRasterState(): GSplatMaterialRasterState {
    return this.rasterState
  }

  setRasterState(state: Partial<GSplatMaterialRasterState>): void {
    this.assertActive()
    const next = Object.freeze({ ...this.rasterState, ...state })
    this.rasterState = next
    this.material.side =
      next.cullMode === 'none'
        ? THREE.DoubleSide
        : next.cullMode === 'back'
          ? THREE.FrontSide
          : THREE.BackSide
    applyMaterialBlendMode(this.material, next.blendMode)
    this.material.depthTest = next.depthTest
    this.material.depthWrite = next.depthWrite
  }

  update(): void {
    this.assertActive()
    this.rebuildNativeSource()
    this.revision++
    this.material.needsUpdate = true
  }

  configureWorkBufferOutputs(
    outputTypes: readonly GSplatTextureFormatInfo['shaderValueType'][]
  ): void {
    this.assertActive()
    this.workBufferOutputTypes = Object.freeze([...outputTypes])
    this.rebuildNativeSource()
    this.material.needsUpdate = true
  }

  private rebuildNativeSource(): void {
    const defineRecord: Record<string, string | number | boolean> = {}
    for (const [name, value] of this.defines) {
      if (value === false || value === null || value === undefined) continue
      defineRecord[toNativeDefineName(name)] =
        value === true ? '' : (value as string | number | boolean)
    }
    const chunkRecord = {
      ...gsplatChunks,
      ...(this.workBufferOutputTypes ? WEBGL_GSPLAT_WORK_BUFFER_CHUNKS : {}),
      ...GSPLAT_SOURCE_FORMAT_CHUNKS.glsl,
      ...Object.fromEntries(this.chunks),
    }
    this.material.defines = defineRecord
    this.material.vertexShader = normalizeShaderSource(
      expandQuotedIncludes(this.vertexSource, chunkRecord),
      'vertex'
    )
    const fragmentSource = normalizeShaderSource(
      expandQuotedIncludes(this.fragmentSource, chunkRecord),
      'fragment'
    )
    this.material.fragmentShader = this.workBufferOutputTypes
      ? `${createFragmentOutputDeclarations(this.workBufferOutputTypes)}\n${fragmentSource}`
      : fragmentSource
    for (const [name, value] of this.parameters) {
      this.material.uniforms[name] = { value: this.owner.unwrapParameter(value) }
    }
    this.material.uniformsNeedUpdate = true
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.parameters.clear()
    this.defines.clear()
    this.chunks.clear()
    this.material.dispose()
    this.finishDispose()
  }
}

/**
 * Adapter-private raster leaf matching PlayCanvas' GSplat MeshInstance envelope.
 *
 * The domain renderer only sees the GSplatDrawObjectLeaf interface; this concrete name makes the
 * ownership boundary explicit without exposing a general Three.js MeshInstance abstraction.
 */
class GSplatRasterLeaf extends WebGLLeaf implements GSplatDrawObjectLeaf {
  readonly object: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial>
  pickable = false
  private visibilityFilter: (camera: THREE.Camera) => boolean = () => true

  constructor(
    owner: WebGLGSplatGraphicsAdapter,
    retire: (leaf: WebGLLeaf) => void,
    material: WebGLMaterialLeaf,
    vertexBuffer: WebGLBufferLeaf,
    indexBuffer: WebGLBufferLeaf | null
  ) {
    super(owner, retire)
    const geometry = new THREE.InstancedBufferGeometry()
    const positions = new Float32Array(
      vertexBuffer.bytes.buffer,
      vertexBuffer.bytes.byteOffset,
      Math.floor(vertexBuffer.bytes.byteLength / Float32Array.BYTES_PER_ELEMENT)
    )
    const positionAttribute = new THREE.BufferAttribute(positions, 3)
    geometry.setAttribute('position', positionAttribute)
    if (indexBuffer) {
      const indices = new Uint32Array(
        indexBuffer.bytes.buffer,
        indexBuffer.bytes.byteOffset,
        Math.floor(indexBuffer.bytes.byteLength / Uint32Array.BYTES_PER_ELEMENT)
      )
      const indexAttribute = new THREE.BufferAttribute(indices, 1)
      geometry.setIndex(indexAttribute)
      indexBuffer.onUpdate(() => {
        indexAttribute.needsUpdate = true
      })
    }
    geometry.instanceCount = 0
    vertexBuffer.onUpdate(() => {
      positionAttribute.needsUpdate = true
    })
    this.object = new THREE.Mesh(geometry, material.material)
    this.object.frustumCulled = false
    this.object.visible = false
  }

  get visible(): boolean {
    return this.object.visible
  }

  set visible(value: boolean) {
    this.object.visible = value
  }

  get instancingCount(): number {
    return this.object.geometry.instanceCount
  }

  set instancingCount(value: number) {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new Error(`非法 WebGL GSplat instancingCount: ${value}`)
    }
    this.object.geometry.instanceCount = value
  }

  get castShadow(): boolean {
    return this.object.castShadow
  }

  set castShadow(value: boolean) {
    this.object.castShadow = value
  }

  setIndirect(drawSlot: number): void {
    if (!Number.isSafeInteger(drawSlot) || drawSlot < -1) {
      throw new Error(`非法 WebGL GSplat indirect draw slot: ${drawSlot}`)
    }
    if (drawSlot >= 0) {
      throw new UnsupportedGraphicsCapabilityError(
        'gsplat.indirect-draw',
        'webgl2',
        'WebGL Unified Quad 仅支持 CPU order texture'
      )
    }
  }

  setMaterial(material: GSplatMaterialLeaf): void {
    this.assertActive()
    this.object.material = this.owner.getMaterialObject(material)
  }

  setVisibilityFilter(filter: (camera: THREE.Camera) => boolean): void {
    this.assertActive()
    this.visibilityFilter = filter
  }

  isVisibleForCamera(camera: THREE.Camera): boolean {
    return this.visible && this.visibilityFilter(camera)
  }

  setBounds(bounds: THREE.Box3 | null): void {
    this.object.geometry.boundingBox = bounds?.clone() ?? null
    if (!bounds) {
      this.object.geometry.boundingSphere = null
      return
    }
    const sphere = new THREE.Sphere()
    bounds.getBoundingSphere(sphere)
    this.object.geometry.boundingSphere = sphere
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.object.removeFromParent()
    this.object.geometry.dispose()
    this.finishDispose()
  }
}

class WebGLProcessingTargetLeaf extends WebGLLeaf implements GSplatProcessingTargetLeaf {
  readonly generation: number
  readonly attachments: readonly GSplatTextureLeaf[]
  readonly target: THREE.WebGLRenderTarget
  private readonly nativeAttachments: readonly WebGLTextureLeaf[]

  constructor(
    owner: WebGLGSplatGraphicsAdapter,
    retire: (leaf: WebGLLeaf) => void,
    descriptor: GSplatProcessingTargetDescriptor,
    attachments: readonly WebGLTextureLeaf[]
  ) {
    super(owner, retire)
    this.generation = owner.generation
    this.attachments = Object.freeze([...descriptor.attachments])
    this.nativeAttachments = Object.freeze([...attachments])
    const first = attachments[0]
    this.target = new THREE.WebGLRenderTarget(first.width, first.height, {
      count: attachments.length,
      depthBuffer: descriptor.withDepth ?? false,
      stencilBuffer: false,
    })
    for (const texture of this.target.textures) {
      texture.renderTarget = null
      texture.dispose()
    }
    this.target.textures = attachments.map((attachment) => attachment.texture)
    attachments.forEach((attachment, index) => {
      attachment.attachReadTarget(this, index)
    })
  }

  markGpuWritten(): void {
    for (const attachment of this.nativeAttachments) attachment.markGpuWritten()
  }

  detach(texture: WebGLTextureLeaf): void {
    texture.detachReadTarget(this)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    for (const attachment of this.nativeAttachments) this.detach(attachment)
    // RenderTarget is only the framebuffer owner. Detach borrowed stream textures before disposal.
    this.target.textures = []
    this.target.dispose()
    this.finishDispose()
  }
}

class WebGLProcessingPipelineLeaf extends WebGLLeaf implements GSplatProcessingPipelineLeaf {
  readonly generation: number
  readonly key: string
  readonly material: THREE.RawShaderMaterial
  readonly scene: THREE.Scene
  readonly camera: THREE.Camera
  readonly quad: THREE.Mesh

  constructor(
    owner: WebGLGSplatGraphicsAdapter,
    retire: (leaf: WebGLLeaf) => void,
    descriptor: GSplatProcessingPipelineDescriptor
  ) {
    super(owner, retire)
    this.generation = owner.generation
    this.key = descriptor.key
    const fragmentShader = createProcessingFragmentShader(descriptor)
    this.material = new THREE.RawShaderMaterial({
      name: descriptor.label,
      glslVersion: THREE.GLSL3,
      vertexShader: FULLSCREEN_VERTEX_SHADER,
      fragmentShader,
      uniforms: {},
      defines: toThreeDefines(descriptor.defines),
      depthTest: false,
      depthWrite: false,
      blending: THREE.NoBlending,
    })
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3)
    )
    this.quad = new THREE.Mesh(geometry, this.material)
    this.quad.frustumCulled = false
    this.scene = new THREE.Scene()
    this.scene.add(this.quad)
    this.camera = new THREE.Camera()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.quad.removeFromParent()
    this.quad.geometry.dispose()
    this.material.dispose()
    this.finishDispose()
  }
}

class WebGLProcessingPassLeaf extends WebGLLeaf implements GSplatProcessingPassLeaf {
  readonly generation: number

  constructor(
    owner: WebGLGSplatGraphicsAdapter,
    retire: (leaf: WebGLLeaf) => void,
    readonly target: WebGLProcessingTargetLeaf,
    readonly pipeline: WebGLProcessingPipelineLeaf
  ) {
    super(owner, retire)
    this.generation = owner.generation
  }

  execute(execution: GSplatProcessingPassExecution): void {
    this.assertActive()
    this.owner.assertGeneration(this.generation)
    this.target.assertActive()
    this.pipeline.assertActive()
    for (const binding of execution.textureBindings) {
      this.pipeline.material.uniforms[binding.name] = {
        value: this.owner.getTextureObject(binding.texture),
      }
    }
    for (const [name, value] of execution.parameters) {
      this.pipeline.material.uniforms[name] = { value: this.owner.unwrapParameter(value) }
    }
    this.pipeline.material.uniformsNeedUpdate = true
    applyBlendState(this.pipeline.material, execution.blendState)
    this.owner.renderProcessingPass(this.target, this.pipeline)
    this.target.markGpuWritten()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.finishDispose()
  }
}

class WebGLWorkBufferSourcePipelineLeaf
  extends WebGLLeaf
  implements GSplatWorkBufferSourcePipelineLeaf
{
  readonly generation: number
  readonly key: string
  readonly scene = new THREE.Scene()
  readonly camera = new THREE.Camera()
  readonly quad: THREE.Mesh<THREE.InstancedBufferGeometry, THREE.RawShaderMaterial>

  constructor(
    owner: WebGLGSplatGraphicsAdapter,
    retire: (leaf: WebGLLeaf) => void,
    descriptor: GSplatWorkBufferSourcePipelineDescriptor,
    readonly material: WebGLMaterialLeaf
  ) {
    super(owner, retire)
    this.generation = owner.generation
    this.key = descriptor.key
    material.configureWorkBufferOutputs(descriptor.outputTypes)
    const geometry = new THREE.InstancedBufferGeometry()
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, -1, 1, 0, 1, 1, 0]), 3)
    )
    geometry.setIndex(new THREE.BufferAttribute(new Uint16Array([0, 1, 2, 2, 1, 3]), 1))
    geometry.instanceCount = 0
    this.quad = new THREE.Mesh(geometry, material.material)
    this.quad.frustumCulled = false
    this.scene.add(this.quad)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.quad.removeFromParent()
    this.quad.geometry.dispose()
    this.finishDispose()
  }
}

class WebGLPickTargetLeaf extends WebGLLeaf implements GSplatPickTargetLease {
  readonly generation: number
  readonly format = 'rgba8unorm' as const
  readonly target: THREE.WebGLRenderTarget

  constructor(
    owner: WebGLGSplatGraphicsAdapter,
    retire: (leaf: WebGLLeaf) => void,
    readonly width: number,
    readonly height: number,
    withDepth: boolean
  ) {
    super(owner, retire)
    this.generation = owner.generation
    this.target = new THREE.WebGLRenderTarget(width, height, {
      format: THREE.RGBAFormat,
      type: THREE.UnsignedByteType,
      internalFormat: 'RGBA8',
      depthBuffer: withDepth,
      stencilBuffer: false,
    })
    this.target.texture.minFilter = THREE.NearestFilter
    this.target.texture.magFilter = THREE.NearestFilter
    this.target.texture.generateMipmaps = false
    this.target.texture.colorSpace = THREE.NoColorSpace
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.target.dispose()
    this.finishDispose()
  }
}

class WebGLRelightingTargetLeaf extends WebGLLeaf implements GSplatRelightingTargetLease {
  readonly generation: number
  readonly target: WebGLProcessingTargetLeaf

  constructor(
    owner: WebGLGSplatGraphicsAdapter,
    retire: (leaf: WebGLLeaf) => void,
    readonly width: number,
    readonly height: number,
    readonly format: GSplatRelightingTargetDescriptor['format'],
    readonly texture: WebGLTextureLeaf,
    target: WebGLProcessingTargetLeaf
  ) {
    super(owner, retire)
    this.generation = owner.generation
    this.target = target
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.target.dispose()
    this.texture.dispose()
    this.finishDispose()
  }
}

export class WebGLGSplatGraphicsAdapter implements GSplatGraphicsAdapter {
  readonly backend = 'webgl2' as const
  readonly language = 'glsl' as const
  readonly limits: GSplatGraphicsLimits
  private readonly renderer: THREE.WebGLRenderer
  private readonly leaves = new Set<WebGLLeaf>()
  private currentGeneration: number
  private disposed = false

  private readonly handleContextLost = (): void => {
    if (this.disposed) return
    this.currentGeneration++
  }

  constructor(readonly graphics: WebGL2Backend) {
    if (graphics.kind !== 'webgl2') throw new BackendMismatchError('webgl2', graphics.kind)
    this.renderer = graphics.getRenderer()
    this.currentGeneration = graphics.generation
    this.limits = readWebGLLimits(this.renderer)
    this.renderer.domElement.addEventListener('webglcontextlost', this.handleContextLost)
  }

  get generation(): number {
    return this.currentGeneration
  }

  getTextureFormatInfo(format: GSplatTextureFormat): GSplatTextureFormatInfo {
    return FORMAT_MAPPINGS[format].info
  }

  validateTextureDescriptor(descriptor: GSplatTextureDescriptor): void {
    this.assertActive()
    if (!descriptor.label.trim()) throw new Error('WebGL GSplat texture label 不能为空')
    if (
      !Number.isSafeInteger(descriptor.width) ||
      !Number.isSafeInteger(descriptor.height) ||
      descriptor.width < 1 ||
      descriptor.height < 1 ||
      descriptor.width > this.limits.maxTextureDimension2D ||
      descriptor.height > this.limits.maxTextureDimension2D
    ) {
      throw new Error(
        `WebGL GSplat texture 尺寸非法: ${descriptor.width}x${descriptor.height}, ` +
          `limit=${this.limits.maxTextureDimension2D}`
      )
    }
    const mapping = FORMAT_MAPPINGS[descriptor.format]
    if (!mapping) throw new Error(`WebGL GSplat texture format 不支持: ${descriptor.format}`)
    if (new Set(descriptor.usages).size !== descriptor.usages.length) {
      throw new Error(`WebGL GSplat texture usage 重复: ${descriptor.label}`)
    }
    if (descriptor.usages.includes('storage')) {
      throw new UnsupportedGraphicsCapabilityError('gsplat.storage-texture', 'webgl2')
    }
    if (descriptor.usages.includes('render-attachment') && !mapping.info.renderable) {
      throw new UnsupportedGraphicsCapabilityError(
        `gsplat.render-attachment.${descriptor.format}`,
        'webgl2'
      )
    }
    const byteLength = descriptor.width * descriptor.height * mapping.info.bytesPerTexel
    if (!Number.isSafeInteger(byteLength) || byteLength > this.limits.maxBufferSize) {
      throw new Error(`WebGL GSplat texture 字节数超限: ${descriptor.label}`)
    }
    if (descriptor.data && descriptor.data.byteLength !== byteLength) {
      throw new Error(
        `WebGL GSplat texture data 长度不匹配: ${descriptor.label}, ` +
          `expected=${byteLength}, actual=${descriptor.data.byteLength}`
      )
    }
  }

  calculateTextureDimensions(numElements: number): GSplatTextureDimensions {
    this.assertActive()
    if (!Number.isSafeInteger(numElements) || numElements < 0) {
      throw new Error(`非法 WebGL GSplat texture element count: ${numElements}`)
    }
    const width = Math.max(1, Math.ceil(Math.sqrt(numElements)))
    const height = Math.max(1, Math.ceil(numElements / width))
    if (width > this.limits.maxTextureDimension2D || height > this.limits.maxTextureDimension2D) {
      throw new Error(
        `WebGL GSplat texture element count 超出维度限制: count=${numElements}, ` +
          `size=${width}x${height}`
      )
    }
    return Object.freeze({ width, height })
  }

  createTexture(descriptor: GSplatTextureDescriptor): GSplatTextureLeaf {
    this.validateTextureDescriptor(descriptor)
    const format = FORMAT_MAPPINGS[descriptor.format]
    const data = format.createArray(descriptor.width * descriptor.height * format.channels)
    if (descriptor.data) copyViewBytes(data, 0, descriptor.data)
    return this.track(new WebGLTextureLeaf(this, this.retireLeaf, descriptor, format, data))
  }

  createBuffer(descriptor: GSplatBufferDescriptor): GSplatBufferLeaf {
    this.assertActive()
    if (!descriptor.label.trim()) throw new Error('WebGL GSplat buffer label 不能为空')
    if (
      !Number.isSafeInteger(descriptor.byteLength) ||
      descriptor.byteLength < 0 ||
      descriptor.byteLength > this.limits.maxBufferSize
    ) {
      throw new Error(`WebGL GSplat buffer 字节数非法: ${descriptor.byteLength}`)
    }
    if (new Set(descriptor.usages).size !== descriptor.usages.length) {
      throw new Error(`WebGL GSplat buffer usage 重复: ${descriptor.label}`)
    }
    if (descriptor.usages.includes('storage') && descriptor.byteLength % 4 !== 0) {
      throw new Error(`WebGL GSplat order/storage buffer 必须 4-byte 对齐: ${descriptor.label}`)
    }
    return this.track(new WebGLBufferLeaf(this, this.retireLeaf, descriptor))
  }

  updateTexture(texture: GSplatTextureLeaf, data: ArrayBufferView): void {
    const leaf = this.requireTexture(texture)
    leaf.assertActive()
    const expected = leaf.width * leaf.height * FORMAT_MAPPINGS[leaf.format].info.bytesPerTexel
    if (data.byteLength !== expected) {
      throw new Error(
        `WebGL GSplat texture upload 长度不匹配: ${leaf.label}, ` +
          `expected=${expected}, actual=${data.byteLength}`
      )
    }
    const mapping = FORMAT_MAPPINGS[leaf.format]
    const target = mapping.createArray(leaf.width * leaf.height * mapping.channels)
    copyViewBytes(target, 0, data)
    leaf.cpuData = target
    leaf.texture.image.data = target
    leaf.texture.needsUpdate = true
  }

  async readTexture(texture: GSplatTextureLeaf, signal?: AbortSignal): Promise<ArrayBufferView> {
    const leaf = this.requireTexture(texture)
    leaf.assertActive()
    throwIfAborted(signal)
    if (leaf.cpuData) return cloneTypedArray(leaf.cpuData)
    const binding = leaf.getReadTarget()
    if (!binding) throw new Error(`WebGL GSplat texture 无 CPU backing/read target: ${leaf.label}`)
    const mapping = FORMAT_MAPPINGS[leaf.format]
    const data = mapping.createArray(leaf.width * leaf.height * mapping.channels)
    await readWebGLRenderTargetPixels(
      this.renderer,
      binding.target.target,
      0,
      0,
      leaf.width,
      leaf.height,
      data,
      0,
      binding.attachment
    )
    throwIfAborted(signal)
    return data
  }

  updateBuffer(buffer: GSplatBufferLeaf, data: ArrayBufferView, byteOffset = 0): void {
    const leaf = this.requireBuffer(buffer)
    leaf.assertActive()
    if (
      !Number.isSafeInteger(byteOffset) ||
      byteOffset < 0 ||
      byteOffset + data.byteLength > leaf.byteLength
    ) {
      throw new Error(
        `WebGL GSplat buffer upload 越界: ${leaf.label}, ` +
          `offset=${byteOffset}, size=${data.byteLength}, capacity=${leaf.byteLength}`
      )
    }
    copyViewBytes(leaf.bytes, byteOffset, data)
    leaf.notifyUpdated()
  }

  createMaterial(input: {
    readonly label: string
    readonly language: GSplatShaderLanguage
    readonly vertexSource: string
    readonly fragmentSource: string
  }): GSplatMaterialLeaf {
    this.assertActive()
    if (input.language !== this.language) {
      throw new Error(`WebGL GSplat material 只接受 GLSL，actual=${input.language}`)
    }
    return this.track(
      new WebGLMaterialLeaf(
        this,
        this.retireLeaf,
        input.label,
        input.vertexSource,
        input.fragmentSource
      )
    )
  }

  createDrawObject(input: {
    readonly label: string
    readonly material: GSplatMaterialLeaf
    readonly vertexBuffer: GSplatBufferLeaf
    readonly indexBuffer?: GSplatBufferLeaf | null
  }): GSplatDrawObjectLeaf {
    this.assertActive()
    const material = this.requireMaterial(input.material)
    const vertexBuffer = this.requireBuffer(input.vertexBuffer)
    const indexBuffer = input.indexBuffer ? this.requireBuffer(input.indexBuffer) : null
    if (
      !vertexBuffer.usages.has('vertex') ||
      (indexBuffer !== null && !indexBuffer.usages.has('index'))
    ) {
      throw new Error(`WebGL GSplat draw object buffer usage 不匹配: ${input.label}`)
    }
    return this.track(
      new GSplatRasterLeaf(this, this.retireLeaf, material, vertexBuffer, indexBuffer)
    )
  }

  createComputePrimitive(_input: GSplatComputePrimitiveDescriptor): GSplatComputePrimitive {
    throw new UnsupportedGraphicsCapabilityError('gsplat.compute', 'webgl2')
  }

  createIndirectPrimitive(_input: {
    readonly label: string
    readonly buffer: GSplatBufferLeaf
  }): GSplatIndirectPrimitive {
    throw new UnsupportedGraphicsCapabilityError('gsplat.indirect-draw', 'webgl2')
  }

  allocateIndirectDrawSlots(_count = 1): never {
    throw new UnsupportedGraphicsCapabilityError('gsplat.indirect-draw', 'webgl2')
  }

  allocateIndirectDispatchSlots(_count = 1): never {
    throw new UnsupportedGraphicsCapabilityError('gsplat.indirect-dispatch', 'webgl2')
  }

  createPrivatePickTarget(descriptor: GSplatPickTargetDescriptor): GSplatPickTargetLease {
    this.validateTargetDimensions(descriptor.width, descriptor.height, 'pick')
    return this.track(
      new WebGLPickTargetLeaf(
        this,
        this.retireLeaf,
        descriptor.width,
        descriptor.height,
        descriptor.withDepth
      )
    )
  }

  async readPrivatePickTarget(
    target: GSplatPickTargetLease,
    region: GSplatPickReadbackRegion,
    signal?: AbortSignal
  ): Promise<Uint8Array> {
    const leaf = this.requirePickTarget(target)
    leaf.assertActive()
    this.validateReadRegion(region, leaf.width, leaf.height)
    throwIfAborted(signal)
    const result = new Uint8Array(region.width * region.height * 4)
    await readWebGLRenderTargetPixels(
      this.renderer,
      leaf.target,
      region.x,
      region.y,
      region.width,
      region.height,
      result
    )
    throwIfAborted(signal)
    return result
  }

  createPrivateRelightingTarget(
    descriptor: GSplatRelightingTargetDescriptor
  ): GSplatRelightingTargetLease {
    this.validateTargetDimensions(descriptor.width, descriptor.height, 'relighting')
    const texture = this.createTexture({
      label: 'gsplat-relighting-target',
      width: descriptor.width,
      height: descriptor.height,
      format: descriptor.format,
      usages: ['sampled', 'render-attachment', 'copy-src'],
    }) as WebGLTextureLeaf
    let target: WebGLProcessingTargetLeaf | null = null
    try {
      target = this.createPrivateProcessingTarget({
        label: 'gsplat-relighting-target',
        attachments: [texture],
        withDepth: descriptor.withDepth,
      }) as WebGLProcessingTargetLeaf
      return this.track(
        new WebGLRelightingTargetLeaf(
          this,
          this.retireLeaf,
          descriptor.width,
          descriptor.height,
          descriptor.format,
          texture,
          target
        )
      )
    } catch (error) {
      target?.dispose()
      texture.dispose()
      throw error
    }
  }

  renderPrivateRelightingScene(
    target: GSplatRelightingTargetLease,
    scene: THREE.Scene,
    camera: THREE.Camera,
    region?: GSplatRelightingRenderRegion
  ): void {
    const leaf = this.requireRelightingTarget(target)
    leaf.assertActive()
    this.graphics.assertCanSubmit()
    const renderTarget = leaf.target.target
    const resolvedRegion = region ?? { x: 0, y: 0, width: leaf.width, height: leaf.height }
    validateRelightingRegion(resolvedRegion, leaf.width, leaf.height)
    const previousTarget = this.renderer.getRenderTarget()
    const previousViewport = this.renderer.getViewport(new THREE.Vector4())
    const previousScissor = this.renderer.getScissor(new THREE.Vector4())
    const previousScissorTest = this.renderer.getScissorTest()
    const previousClearColor = this.renderer.getClearColor(new THREE.Color())
    const previousClearAlpha = this.renderer.getClearAlpha()
    const previousAutoClear = this.renderer.autoClear
    try {
      renderTarget.viewport.set(
        resolvedRegion.x,
        resolvedRegion.y,
        resolvedRegion.width,
        resolvedRegion.height
      )
      renderTarget.scissor.copy(renderTarget.viewport)
      renderTarget.scissorTest = true
      this.renderer.setRenderTarget(renderTarget)
      this.renderer.setClearColor(0, 0)
      this.renderer.autoClear = false
      this.renderer.clear(true, true, false)
      this.renderer.render(scene, camera)
      leaf.target.markGpuWritten()
    } finally {
      this.renderer.autoClear = previousAutoClear
      this.renderer.setClearColor(previousClearColor, previousClearAlpha)
      this.renderer.setRenderTarget(previousTarget)
      this.renderer.setViewport(previousViewport)
      this.renderer.setScissor(previousScissor)
      this.renderer.setScissorTest(previousScissorTest)
    }
  }

  createPrivateProcessingTarget(
    descriptor: GSplatProcessingTargetDescriptor
  ): GSplatProcessingTargetLeaf {
    this.assertActive()
    if (descriptor.attachments.length < 1) {
      throw new Error(`WebGL GSplat processing target 缺少 attachment: ${descriptor.label}`)
    }
    if (descriptor.attachments.length > this.limits.maxColorAttachments) {
      throw new Error(
        `WebGL GSplat processing target attachment 超限: ${descriptor.attachments.length}`
      )
    }
    const attachments = descriptor.attachments.map((attachment) => this.requireTexture(attachment))
    const first = attachments[0]
    let bytesPerSample = 0
    for (const attachment of attachments) {
      if (attachment.width !== first.width || attachment.height !== first.height) {
        throw new Error(`WebGL GSplat processing target attachment 尺寸不一致: ${descriptor.label}`)
      }
      const info = this.getTextureFormatInfo(attachment.format)
      if (!info.renderable) {
        throw new Error(`WebGL GSplat processing target format 不可渲染: ${attachment.format}`)
      }
      bytesPerSample += info.bytesPerTexel
    }
    if (bytesPerSample > this.limits.maxColorAttachmentBytesPerSample) {
      throw new Error(`WebGL GSplat MRT bytes/sample 超限: ${bytesPerSample}`)
    }
    return this.track(new WebGLProcessingTargetLeaf(this, this.retireLeaf, descriptor, attachments))
  }

  createPrivateProcessingPipeline(
    descriptor: GSplatProcessingPipelineDescriptor
  ): GSplatProcessingPipelineLeaf {
    this.assertActive()
    if (descriptor.language !== this.language) {
      throw new Error(`WebGL GSplat processing pipeline 只接受 GLSL: ${descriptor.label}`)
    }
    if (descriptor.outputTypes.length < 1) {
      throw new Error(`WebGL GSplat processing pipeline 缺少输出: ${descriptor.label}`)
    }
    return this.track(new WebGLProcessingPipelineLeaf(this, this.retireLeaf, descriptor))
  }

  createPrivateProcessingPass(input: {
    readonly label: string
    readonly target: GSplatProcessingTargetLeaf
    readonly pipeline: GSplatProcessingPipelineLeaf
  }): GSplatProcessingPassLeaf {
    this.assertActive()
    const target = this.requireProcessingTarget(input.target)
    const pipeline = this.requireProcessingPipeline(input.pipeline)
    if (
      target.attachments.length !==
      pipeline.material.fragmentShader
        .match(/pcFragColor\d+/g)
        ?.filter((value, index, all) => all.indexOf(value) === index).length
    ) {
      throw new Error(`WebGL GSplat processing pass target/pipeline 输出数量不匹配: ${input.label}`)
    }
    return this.track(new WebGLProcessingPassLeaf(this, this.retireLeaf, target, pipeline))
  }

  createPrivateWorkBufferSourcePipeline(
    descriptor: GSplatWorkBufferSourcePipelineDescriptor
  ): GSplatWorkBufferSourcePipelineLeaf {
    this.assertActive()
    if (descriptor.language !== this.language || !descriptor.instanced) {
      throw new Error(
        `WebGL GSplat WorkBuffer source pipeline descriptor 非法: ${descriptor.label}`
      )
    }
    const material = this.requireMaterial(descriptor.material)
    return this.track(
      new WebGLWorkBufferSourcePipelineLeaf(this, this.retireLeaf, descriptor, material)
    )
  }

  submit(primitives: readonly GSplatSubmitPrimitive[]): void {
    this.assertActive()
    for (const entry of primitives) {
      if (entry.kind === 'compute') {
        throw new UnsupportedGraphicsCapabilityError('gsplat.compute', 'webgl2')
      }
      const primitive = entry.primitive as { execute?: () => void }
      if (typeof primitive.execute !== 'function') {
        throw new Error(`WebGL GSplat submit primitive 缺少 execute(): ${entry.kind}`)
      }
      primitive.execute()
    }
  }

  getTextureObject(texture: GSplatTextureLeaf): THREE.DataTexture {
    return this.requireTexture(texture).texture
  }

  /**
   * Three DataTexture 默认延迟到首次采样才上传；GPU-only source 释放前必须显式完成上传。
   * PlayCanvas texture update 本身是立即提交语义，WebGL adapter 在这里补齐同一生命周期契约。
   */
  uploadTextureBeforeSourceRelease(texture: WebGLTextureLeaf): void {
    this.assertOwner(texture)
    texture.assertActive()
    if (texture.texture.image.data) this.renderer.initTexture(texture.texture)
  }

  /** 防止 Three 把 attachment 初始 DataTexture backing 在后续采样时重新上传并覆盖 MRT 结果。 */
  markTextureGpuWritten(texture: WebGLTextureLeaf): void {
    this.assertOwner(texture)
    texture.assertActive()
    this.renderer.properties.update(texture.texture, '__version', texture.texture.version)
  }

  getBufferTexture(buffer: GSplatBufferLeaf): THREE.DataTexture {
    const leaf = this.requireBuffer(buffer)
    if (!leaf.storageTexture)
      throw new Error(`WebGL GSplat buffer 不是 texture-backed: ${leaf.label}`)
    return leaf.storageTexture
  }

  getMaterialObject(material: GSplatMaterialLeaf): THREE.RawShaderMaterial {
    return this.requireMaterial(material).material
  }

  getDrawObject(draw: GSplatDrawObjectLeaf): THREE.Mesh {
    return this.requireDrawObject(draw).object
  }

  /** WebGL Unified 同步 picker 的私有 ID pass；不暴露为通用 GraphicsBackend offscreen API。 */
  renderPrivatePickScene(
    target: GSplatPickTargetLease,
    scene: THREE.Scene,
    camera: THREE.Camera
  ): void {
    const leaf = this.requirePickTarget(target)
    leaf.assertActive()
    this.graphics.assertCanSubmit()
    const previousTarget = this.renderer.getRenderTarget()
    const previousViewport = this.renderer.getViewport(new THREE.Vector4())
    const previousScissor = this.renderer.getScissor(new THREE.Vector4())
    const previousScissorTest = this.renderer.getScissorTest()
    const previousClearColor = this.renderer.getClearColor(new THREE.Color())
    const previousClearAlpha = this.renderer.getClearAlpha()
    const previousAutoClear = this.renderer.autoClear
    try {
      leaf.target.viewport.set(0, 0, leaf.width, leaf.height)
      leaf.target.scissor.set(0, 0, leaf.width, leaf.height)
      leaf.target.scissorTest = true
      this.renderer.setRenderTarget(leaf.target)
      this.renderer.setClearColor(0xffffff, 1)
      this.renderer.autoClear = false
      this.renderer.clear(true, true, false)
      this.renderer.render(scene, camera)
    } finally {
      this.renderer.autoClear = previousAutoClear
      this.renderer.setClearColor(previousClearColor, previousClearAlpha)
      this.renderer.setRenderTarget(previousTarget)
      this.renderer.setViewport(previousViewport)
      this.renderer.setScissor(previousScissor)
      this.renderer.setScissorTest(previousScissorTest)
    }
  }

  /** WebGL 的既有同步 picking 兼容边界；WebGPU 不得实现同步 busy-wait。 */
  readPrivatePickTargetSync(
    target: GSplatPickTargetLease,
    region: GSplatPickReadbackRegion
  ): Uint8Array {
    const leaf = this.requirePickTarget(target)
    leaf.assertActive()
    this.validateReadRegion(region, leaf.width, leaf.height)
    const result = new Uint8Array(region.width * region.height * 4)
    unbindWebGLPixelPackBuffer(this.renderer)
    this.renderer.readRenderTargetPixels(
      leaf.target,
      region.x,
      region.y,
      region.width,
      region.height,
      result
    )
    return result
  }

  unwrapParameter(value: unknown): unknown {
    if (value instanceof WebGLTextureLeaf) {
      this.assertOwner(value)
      return value.texture
    }
    if (value instanceof WebGLBufferLeaf) {
      this.assertOwner(value)
      if (!value.storageTexture) {
        throw new Error(`WebGL GSplat material 不能绑定非 texture-backed buffer: ${value.label}`)
      }
      return value.storageTexture
    }
    return value
  }

  renderProcessingPass(
    target: WebGLProcessingTargetLeaf,
    pipeline: WebGLProcessingPipelineLeaf
  ): void {
    this.assertActive()
    this.graphics.assertCanSubmit()
    this.renderSceneToTarget(target, pipeline.scene, pipeline.camera)
  }

  executeWorkBufferSource(input: {
    readonly target: GSplatProcessingTargetLeaf
    readonly pipeline: GSplatWorkBufferSourcePipelineLeaf
    readonly parameters: readonly (readonly [string, unknown])[]
    readonly instanceCount: number
  }): void {
    this.executeWorkBufferSources([input])
  }

  /** PlayCanvas WorkBuffer render pass 对齐：同一 MRT 只绑定一次，再连续提交全部 Resource。 */
  executeWorkBufferSources(inputs: readonly WebGLWorkBufferCommand[]): void {
    this.assertActive()
    if (inputs.length === 0) return
    const target = this.requireProcessingTarget(inputs[0].target)
    target.assertActive()
    this.graphics.assertCanSubmit()
    const previousTarget = this.renderer.getRenderTarget()
    const previousViewport = this.renderer.getViewport(new THREE.Vector4())
    const previousScissor = this.renderer.getScissor(new THREE.Vector4())
    const previousScissorTest = this.renderer.getScissorTest()
    const previousAutoClear = this.renderer.autoClear
    try {
      // RenderTarget viewport/scissor 使用物理纹素；Three 的公开 setViewport/setScissor 会乘
      // renderer pixelRatio，在 DPR > 1 时把 WorkBuffer 视口放大并裁掉高位 allocation。
      target.target.viewport.set(0, 0, target.target.width, target.target.height)
      target.target.scissor.set(0, 0, target.target.width, target.target.height)
      target.target.scissorTest = true
      this.renderer.setRenderTarget(target.target)
      this.renderer.autoClear = false
      for (const input of inputs) {
        if (this.requireProcessingTarget(input.target) !== target) {
          throw new Error('WebGL GSplat WorkBuffer batch target 不一致')
        }
        const pipeline = this.requireWorkBufferPipeline(input.pipeline)
        pipeline.assertActive()
        this.assertGeneration(pipeline.generation)
        if (!Number.isSafeInteger(input.instanceCount) || input.instanceCount < 0) {
          throw new Error(`非法 WebGL GSplat WorkBuffer instanceCount: ${input.instanceCount}`)
        }
        for (const [name, value] of input.parameters) {
          pipeline.material.material.uniforms[name] = { value: this.unwrapParameter(value) }
        }
        pipeline.material.material.uniformsNeedUpdate = true
        pipeline.quad.geometry.instanceCount = input.instanceCount
        this.renderer.render(pipeline.scene, pipeline.camera)
      }
      target.markGpuWritten()
    } finally {
      this.renderer.setRenderTarget(previousTarget)
      this.renderer.setViewport(previousViewport)
      this.renderer.setScissor(previousScissor)
      this.renderer.setScissorTest(previousScissorTest)
      this.renderer.autoClear = previousAutoClear
    }
  }

  private renderSceneToTarget(
    target: WebGLProcessingTargetLeaf,
    scene: THREE.Scene,
    camera: THREE.Camera
  ): void {
    const previousTarget = this.renderer.getRenderTarget()
    const previousViewport = this.renderer.getViewport(new THREE.Vector4())
    const previousScissor = this.renderer.getScissor(new THREE.Vector4())
    const previousScissorTest = this.renderer.getScissorTest()
    const previousAutoClear = this.renderer.autoClear
    try {
      target.target.viewport.set(0, 0, target.target.width, target.target.height)
      target.target.scissor.set(0, 0, target.target.width, target.target.height)
      target.target.scissorTest = true
      this.renderer.setRenderTarget(target.target)
      this.renderer.autoClear = false
      this.renderer.render(scene, camera)
    } finally {
      this.renderer.setRenderTarget(previousTarget)
      this.renderer.setViewport(previousViewport)
      this.renderer.setScissor(previousScissor)
      this.renderer.setScissorTest(previousScissorTest)
      this.renderer.autoClear = previousAutoClear
    }
  }

  assertGeneration(generation: number): void {
    if (generation !== this.generation) {
      throw new Error(
        `WebGL GSplat leaf generation 已失效: leaf=${generation}, current=${this.generation}`
      )
    }
  }

  assertActive(): void {
    if (this.disposed) throw new Error('WebGLGSplatGraphicsAdapter 已销毁')
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.currentGeneration++
    this.renderer.domElement.removeEventListener('webglcontextlost', this.handleContextLost)
    for (const leaf of [...this.leaves].reverse()) leaf.dispose()
    this.leaves.clear()
  }

  private readonly retireLeaf = (leaf: WebGLLeaf): void => {
    this.leaves.delete(leaf)
  }

  private track<T extends WebGLLeaf>(leaf: T): T {
    this.leaves.add(leaf)
    return leaf
  }

  private assertOwner(leaf: WebGLLeaf): void {
    if (leaf.owner !== this) throw new Error('WebGL GSplat GPU leaf 来自另一个 graphics device')
  }

  private requireTexture(texture: GSplatTextureLeaf): WebGLTextureLeaf {
    if (!(texture instanceof WebGLTextureLeaf)) {
      throw new Error('WebGL GSplat texture leaf 类型不匹配')
    }
    this.assertOwner(texture)
    return texture
  }

  private requireBuffer(buffer: GSplatBufferLeaf): WebGLBufferLeaf {
    if (!(buffer instanceof WebGLBufferLeaf)) throw new Error('WebGL GSplat buffer leaf 类型不匹配')
    this.assertOwner(buffer)
    return buffer
  }

  private requireMaterial(material: GSplatMaterialLeaf): WebGLMaterialLeaf {
    if (!(material instanceof WebGLMaterialLeaf)) {
      throw new Error('WebGL GSplat material leaf 类型不匹配')
    }
    this.assertOwner(material)
    return material
  }

  private requireDrawObject(draw: GSplatDrawObjectLeaf): GSplatRasterLeaf {
    if (!(draw instanceof GSplatRasterLeaf)) {
      throw new Error('WebGL GSplat draw object leaf 类型不匹配')
    }
    this.assertOwner(draw)
    return draw
  }

  private requirePickTarget(target: GSplatPickTargetLease): WebGLPickTargetLeaf {
    if (!(target instanceof WebGLPickTargetLeaf)) {
      throw new Error('WebGL GSplat pick target leaf 类型不匹配')
    }
    this.assertOwner(target)
    return target
  }

  private requireProcessingTarget(target: GSplatProcessingTargetLeaf): WebGLProcessingTargetLeaf {
    if (!(target instanceof WebGLProcessingTargetLeaf)) {
      throw new Error('WebGL GSplat processing target leaf 类型不匹配')
    }
    this.assertOwner(target)
    return target
  }

  private requireRelightingTarget(target: GSplatRelightingTargetLease): WebGLRelightingTargetLeaf {
    if (!(target instanceof WebGLRelightingTargetLeaf)) {
      throw new Error('WebGL GSplat relighting target leaf 类型不匹配')
    }
    this.assertOwner(target)
    return target
  }

  private requireProcessingPipeline(
    pipeline: GSplatProcessingPipelineLeaf
  ): WebGLProcessingPipelineLeaf {
    if (!(pipeline instanceof WebGLProcessingPipelineLeaf)) {
      throw new Error('WebGL GSplat processing pipeline leaf 类型不匹配')
    }
    this.assertOwner(pipeline)
    return pipeline
  }

  private requireWorkBufferPipeline(
    pipeline: GSplatWorkBufferSourcePipelineLeaf
  ): WebGLWorkBufferSourcePipelineLeaf {
    if (!(pipeline instanceof WebGLWorkBufferSourcePipelineLeaf)) {
      throw new Error('WebGL GSplat WorkBuffer pipeline leaf 类型不匹配')
    }
    this.assertOwner(pipeline)
    return pipeline
  }

  private validateTargetDimensions(width: number, height: number, label: string): void {
    if (
      !Number.isSafeInteger(width) ||
      !Number.isSafeInteger(height) ||
      width < 1 ||
      height < 1 ||
      width > this.limits.maxTextureDimension2D ||
      height > this.limits.maxTextureDimension2D
    ) {
      throw new Error(`WebGL GSplat ${label} target 尺寸非法: ${width}x${height}`)
    }
  }

  private validateReadRegion(
    region: GSplatPickReadbackRegion,
    width: number,
    height: number
  ): void {
    if (
      !Number.isSafeInteger(region.x) ||
      !Number.isSafeInteger(region.y) ||
      !Number.isSafeInteger(region.width) ||
      !Number.isSafeInteger(region.height) ||
      region.x < 0 ||
      region.y < 0 ||
      region.width < 1 ||
      region.height < 1 ||
      region.x + region.width > width ||
      region.y + region.height > height
    ) {
      throw new Error(
        `WebGL GSplat readback region 越界: ${region.x},${region.y},${region.width}x${region.height}`
      )
    }
  }
}

function validateRelightingRegion(
  region: GSplatRelightingRenderRegion,
  width: number,
  height: number
): void {
  if (
    !Number.isSafeInteger(region.x) ||
    !Number.isSafeInteger(region.y) ||
    !Number.isSafeInteger(region.width) ||
    !Number.isSafeInteger(region.height) ||
    region.x < 0 ||
    region.y < 0 ||
    region.width < 1 ||
    region.height < 1 ||
    region.x + region.width > width ||
    region.y + region.height > height
  ) {
    throw new Error(
      `WebGL GSplat relighting region 越界: ${region.x},${region.y},${region.width}x${region.height}`
    )
  }
}

interface WebGLWorkBufferCommand {
  readonly target: GSplatProcessingTargetLeaf
  readonly pipeline: GSplatWorkBufferSourcePipelineLeaf
  readonly parameters: readonly (readonly [string, unknown])[]
  readonly instanceCount: number
}

interface WorkBufferReadableResource {
  readonly format: {
    readonly hash: number
    getInputDeclarations(streamNames?: readonly string[], language?: GSplatShaderLanguage): string
  }
  readonly supportsWorkBufferGeometry: boolean
  getWorkBufferRenderInfo(
    colorOnly: boolean,
    modifier: Readonly<{ code: string; hash: number }> | null,
    formatHash: number,
    formatDeclarations: string,
    workBufferFormat: Parameters<GSplatWorkBufferPassFactory['create']>[0]['format']
  ): GSplatWorkBufferRenderInfo
}

/** WebGL WorkBuffer source-copy pass factory，保持 PlayCanvas 的 format-reader/sub-draw 结构。 */
export class WebGLGSplatWorkBufferPassFactory implements GSplatWorkBufferPassFactory {
  constructor(readonly adapter: WebGLGSplatGraphicsAdapter) {}

  create(input: Parameters<GSplatWorkBufferPassFactory['create']>[0]): GSplatWorkBufferPass {
    if (input.adapter !== this.adapter || input.streams.graphicsAdapter !== this.adapter) {
      throw new Error('WebGL GSplat WorkBuffer pass 与 adapter 不属于同一 graphics device')
    }
    if (input.format.graphicsAdapter !== this.adapter) {
      throw new Error('WebGL GSplat WorkBuffer format 与 adapter 不属于同一 graphics device')
    }
    return new WebGLGSplatWorkBufferPass(this.adapter, input.streams, input.format, input.mode)
  }
}

class WebGLGSplatWorkBufferPass implements GSplatWorkBufferPass {
  readonly mode: GSplatWorkBufferPassMode
  private readonly target: GSplatProcessingTargetLeaf
  private disposed = false

  constructor(
    private readonly adapter: WebGLGSplatGraphicsAdapter,
    private readonly streams: GSplatStreams,
    private readonly format: Parameters<GSplatWorkBufferPassFactory['create']>[0]['format'],
    mode: GSplatWorkBufferPassMode
  ) {
    this.mode = mode
    const attachments =
      mode === 'colorOnly'
        ? [this.requireStreamTexture(streams, 'dataColor')]
        : streams.getTexturesInOrder()
    this.target = adapter.createPrivateProcessingTarget({
      label: `gsplat-work-buffer:${mode}`,
      attachments,
    })
  }

  stage(input: GSplatWorkBufferCopyInput): GSplatWorkBufferCopyStage {
    this.assertActive()
    const textureSize = this.requireTextureSize()
    const transientTextures: GSplatTextureLeaf[] = []
    const commands: WebGLWorkBufferCommand[] = []
    try {
      for (const splat of input.splats) {
        if (splat.activeSplats <= 0) continue
        const subDraw = this.resolveSubDraw(splat, textureSize, input.changedAllocIds)
        if (!subDraw) continue
        if (subDraw.transient) transientTextures.push(subDraw.texture)
        commands.push(
          this.createCommand(input.camera, splat, subDraw.texture, subDraw.count, subDraw.base)
        )
      }
    } catch (error) {
      for (const texture of transientTextures) texture.dispose()
      throw error
    }

    let committed = false
    let settled = false
    return {
      commit: () => {
        if (settled) throw new Error('WebGL GSplat WorkBuffer copy stage 已结束')
        if (committed) return
        this.adapter.executeWorkBufferSources(commands)
        committed = true
      },
      rollback: () => {
        if (settled) return
        settled = true
        for (const texture of transientTextures) texture.dispose()
        committed = false
      },
      finalize: () => {
        if (settled) return
        settled = true
        for (const texture of transientTextures) texture.dispose()
      },
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.target.dispose()
  }

  private createCommand(
    camera: THREE.Camera,
    splat: GSplatInfo,
    subDrawTexture: GSplatTextureLeaf,
    subDrawCount: number,
    subDrawBase: number
  ): WebGLWorkBufferCommand {
    const resource = this.requireResource(splat)
    const modifier = splat.getWorkBufferModifier()
    const renderInfo = resource.getWorkBufferRenderInfo(
      this.mode === 'colorOnly',
      modifier,
      resource.format.hash,
      resource.format.getInputDeclarations(undefined, 'glsl'),
      this.format
    )
    const modelMatrix = splat.node.matrixWorld.clone()
    const sceneOrigin = (splat.node as THREE.Object3D & { __sceneOriginOverride?: THREE.Vector3 })
      .__sceneOriginOverride
    if (sceneOrigin) {
      modelMatrix.elements[12] -= sceneOrigin.x
      modelMatrix.elements[13] -= sceneOrigin.y
      modelMatrix.elements[14] -= sceneOrigin.z
    }
    const modelScale = new THREE.Vector3()
    const modelRotation = new THREE.Quaternion()
    const modelPosition = new THREE.Vector3()
    modelMatrix.decompose(modelPosition, modelRotation, modelScale)
    if (modelRotation.w < 0) {
      modelRotation.set(-modelRotation.x, -modelRotation.y, -modelRotation.z, -modelRotation.w)
    }
    camera.updateWorldMatrix(true, false)
    const viewMatrix = camera.matrixWorld.clone().invert()
    if (sceneOrigin) {
      viewMatrix.multiply(
        new THREE.Matrix4().makeTranslation(sceneOrigin.x, sceneOrigin.y, sceneOrigin.z)
      )
    }
    const textureSize = this.requireTextureSize()
    const scalarFilter = splat.getScalarFilter()
    const parameters: Array<readonly [string, unknown]> = [
      ['matrix_view', viewMatrix],
      ['matrix_model', modelMatrix],
      ['model_scale', modelScale],
      ['model_rotation', modelRotation],
      ['uColorMultiply', new THREE.Vector3(1, 1, 1)],
      ['uScalarFilterMode', resolveScalarFilterMode(scalarFilter?.type)],
      ['uScalarFilterRange', new THREE.Vector2(scalarFilter?.min ?? 0, scalarFilter?.max ?? 0)],
      ['uScalarFilterExempt', splat.getIsEnvironment()],
      ['uId', splat.placementId],
      ['uSubDrawData', subDrawTexture],
      ['uSubDrawBase', subDrawBase],
      ['uTextureSize', new THREE.Vector2(textureSize, textureSize)],
    ]
    if (this.mode === 'colorOnly' && resource.supportsWorkBufferGeometry) {
      const transformA = this.requireStreamTexture(this.streams, 'dataTransformA')
      const transformB = this.requireStreamTexture(this.streams, 'dataTransformB')
      parameters.push(
        ['uWorkBufferTransformA', transformA],
        ['uWorkBufferTransformB', transformB],
        ['matrix_model_inverse', modelMatrix.clone().invert()],
        [
          'uCameraPosition',
          sceneOrigin
            ? camera.getWorldPosition(new THREE.Vector3()).sub(sceneOrigin)
            : camera.getWorldPosition(new THREE.Vector3()),
        ]
      )
    }
    for (const [name, parameter] of splat.parameters ?? []) {
      parameters.push([name, snapshotUniformValue(parameter.data)])
    }
    const instanceStreams = splat.getInstanceStreams()
    if (instanceStreams) {
      instanceStreams.syncWithFormat(splat.resource.format)
      for (const stream of splat.resource.format.instanceStreams) {
        const texture = instanceStreams.getTexture(stream.name)
        if (!texture) throw new Error(`WebGL GSplat instance stream 缺失: ${stream.name}`)
        parameters.push([stream.name, texture])
      }
    }
    return Object.freeze({
      target: this.target,
      pipeline: renderInfo.pipeline,
      parameters: Object.freeze(parameters),
      instanceCount: subDrawCount,
    })
  }

  private resolveSubDraw(
    splat: GSplatInfo,
    textureSize: number,
    changedAllocIds: ReadonlySet<number> | null
  ): Readonly<{
    texture: GSplatTextureLeaf
    count: number
    base: number
    transient: boolean
  }> | null {
    if (!changedAllocIds || splat.intervals.length === 0) {
      if (changedAllocIds && !changedAllocIds.has(splat.allocId)) return null
      splat.ensureSubDrawTexture(textureSize)
      if (!splat.subDrawTexture || splat.subDrawCount < 1) return null
      return {
        texture: splat.subDrawTexture,
        count: splat.subDrawCount,
        base: 0,
        transient: false,
      }
    }

    const maxEntries = (splat.intervals.length / 2) * 3
    const data = new Uint32Array(maxEntries * 4)
    let count = 0
    for (let index = 0; index < splat.intervals.length / 2; index++) {
      if (!changedAllocIds.has(splat.intervalAllocIds[index])) continue
      count = splat.appendSubDraws(
        data,
        count,
        splat.intervals[index * 2],
        splat.intervals[index * 2 + 1] - splat.intervals[index * 2],
        splat.intervalOffsets[index],
        textureSize
      )
    }
    if (count === 0) return null
    const dimensions = this.adapter.calculateTextureDimensions(count)
    const upload = new Uint32Array(dimensions.width * dimensions.height * 4)
    upload.set(data.subarray(0, count * 4))
    const texture = this.adapter.createTexture({
      label: `gsplat-partial-sub-draw:${splat.placementId}`,
      format: 'rgba32uint',
      width: dimensions.width,
      height: dimensions.height,
      usages: ['sampled', 'copy-dst'],
    })
    try {
      this.adapter.updateTexture(texture, upload)
      return { texture, count, base: 0, transient: true }
    } catch (error) {
      texture.dispose()
      throw error
    }
  }

  private requireResource(splat: GSplatInfo): WorkBufferReadableResource {
    const resource = splat.resource as GSplatInfo['resource'] & Partial<WorkBufferReadableResource>
    if (typeof resource.getWorkBufferRenderInfo !== 'function') {
      throw new Error(`WebGL GSplat Resource 缺少 WorkBuffer reader: ${resource.id}`)
    }
    return resource as WorkBufferReadableResource
  }

  private requireTextureSize(): number {
    const texture = this.requireStreamTexture(this.streams, 'dataColor')
    if (texture.width !== texture.height) {
      throw new Error(
        `WebGL GSplat WorkBuffer 必须为方形 texture: ${texture.width}x${texture.height}`
      )
    }
    return texture.width
  }

  private requireStreamTexture(streams: GSplatStreams, name: string): GSplatTextureLeaf {
    const texture = streams.getTexture(name)
    if (!texture) throw new Error(`WebGL GSplat WorkBuffer stream 缺失: ${name}`)
    return texture
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('WebGL GSplat WorkBuffer pass 已销毁')
  }
}

export class WebGLGSplatGraphicsAdapterFactory implements GSplatGraphicsAdapterFactory {
  create(graphics: GraphicsBackend): GSplatGraphicsAdapter {
    if (graphics.kind !== 'webgl2') throw new BackendMismatchError('webgl2', graphics.kind)
    if (!(graphics instanceof WebGL2Backend)) {
      throw new Error('WebGLGSplatGraphicsAdapterFactory 要求 engine-owned WebGL2Backend')
    }
    return new WebGLGSplatGraphicsAdapter(graphics)
  }
}

function toMinFilter(value?: 'nearest' | 'linear'): THREE.MinificationTextureFilter {
  return value === 'linear' ? THREE.LinearFilter : THREE.NearestFilter
}

function toMagFilter(value?: 'nearest' | 'linear'): THREE.MagnificationTextureFilter {
  return value === 'linear' ? THREE.LinearFilter : THREE.NearestFilter
}

function toWrapping(value?: 'clamp-to-edge' | 'repeat' | 'mirror-repeat'): THREE.Wrapping {
  if (value === 'repeat') return THREE.RepeatWrapping
  if (value === 'mirror-repeat') return THREE.MirroredRepeatWrapping
  return THREE.ClampToEdgeWrapping
}

function copyViewBytes(target: ArrayBufferView, byteOffset: number, source: ArrayBufferView): void {
  new Uint8Array(target.buffer, target.byteOffset + byteOffset, source.byteLength).set(
    new Uint8Array(source.buffer, source.byteOffset, source.byteLength)
  )
}

function cloneTypedArray<T extends TextureArray>(source: T): T {
  return source.slice() as T
}

function snapshotUniformValue(value: unknown): unknown {
  if (
    value instanceof THREE.Vector2 ||
    value instanceof THREE.Vector3 ||
    value instanceof THREE.Vector4 ||
    value instanceof THREE.Quaternion ||
    value instanceof THREE.Matrix3 ||
    value instanceof THREE.Matrix4 ||
    value instanceof THREE.Color
  ) {
    return value.clone()
  }
  if (Array.isArray(value)) return Object.freeze(value.map(snapshotUniformValue))
  if (ArrayBuffer.isView(value)) {
    const slice = (value as ArrayBufferView & { slice?: () => ArrayBufferView }).slice
    return typeof slice === 'function' ? slice.call(value) : value
  }
  return value
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('GSplat GPU 操作已取消', 'AbortError')
}

function normalizeShaderSource(source: string, stage: 'vertex' | 'fragment'): string {
  const normalized = source.trim()
    ? source
    : stage === 'vertex'
      ? 'in vec3 position; void main() { gl_Position = vec4(position, 1.0); }'
      : 'layout(location = 0) out vec4 pcFragColor0; void main() { pcFragColor0 = vec4(0.0); }'
  return `precision highp float;\nprecision highp int;\n${normalized}`
}

function expandQuotedIncludes(
  source: string,
  chunks: Readonly<Record<string, string | undefined>>,
  ancestors: ReadonlySet<string> = new Set()
): string {
  return source.replace(/#include\s+"([^"]+)"/g, (statement, name: string) => {
    const chunk = chunks[name]
    if (chunk === undefined || ancestors.has(name)) return statement
    const next = new Set(ancestors)
    next.add(name)
    return expandQuotedIncludes(chunk, chunks, next)
  })
}

const FULLSCREEN_VERTEX_SHADER = /* glsl */ `
in vec3 position;
void main(void) {
  gl_Position = vec4(position, 1.0);
}
`

function createProcessingFragmentShader(descriptor: GSplatProcessingPipelineDescriptor): string {
  const outputs = createFragmentOutputDeclarations(descriptor.outputTypes)
  const chunks = {
    ...GSPLAT_SOURCE_FORMAT_CHUNKS.glsl,
    gsplatPackingPS: GSPLAT_SOURCE_FORMAT_CHUNKS.glsl.gsplatPackingPS,
  }
  if (/\bvoid\s+main\s*\(/.test(descriptor.processSource)) {
    return expandQuotedIncludes(
      `precision highp float;\nprecision highp int;\n${outputs}\n${descriptor.processSource}`,
      chunks
    )
  }
  const source = /* glsl */ `
precision highp float;
precision highp int;
${outputs}
uniform highp uint splatTextureSize;
uniform highp uint dstTextureSize;
uniform highp uint srcNumSplats;
uniform highp uint dstNumSplats;
struct SplatRef { uint index; ivec2 uv; };
SplatRef splat;
void setSplat(uint index) {
  splat.index = index;
  splat.uv = ivec2(int(index % splatTextureSize), int(index / splatTextureSize));
}
${descriptor.inputDeclarations}
${descriptor.outputDeclarations}
${descriptor.readCode}
${descriptor.processSource}
void main(void) {
  ivec2 fragCoords = ivec2(gl_FragCoord.xy);
  uint splatIndex = uint(fragCoords.y * int(dstTextureSize) + fragCoords.x);
  if (splatIndex >= dstNumSplats) discard;
  setSplat(splatIndex);
  process();
}
`
  return expandQuotedIncludes(source, chunks)
}

function createFragmentOutputDeclarations(
  outputTypes: readonly GSplatTextureFormatInfo['shaderValueType'][]
): string {
  return outputTypes
    .map((type, index) => {
      const glslType = type === 'uint' || type === 'vec4u' ? 'uvec4' : 'vec4'
      return `layout(location = ${index}) out highp ${glslType} pcFragColor${index};`
    })
    .join('\n')
}

function toThreeDefines(
  defines: readonly (readonly [string, unknown])[]
): Record<string, string | number | boolean> {
  const result: Record<string, string | number | boolean> = {}
  for (const [name, value] of defines) {
    if (value === false || value === null || value === undefined) continue
    result[toNativeDefineName(name)] = value === true ? '' : (value as string | number | boolean)
  }
  return result
}

/** PlayCanvas ShaderMaterial 的 `{NAME}` 注入键在 Three RawShaderMaterial 中对应普通宏名。 */
function toNativeDefineName(name: string): string {
  const placeholder = /^\{([A-Za-z_][A-Za-z0-9_]*)\}$/.exec(name)
  return placeholder?.[1] ?? name
}

function applyBlendState(material: THREE.RawShaderMaterial, state: unknown): void {
  if (state === null || state === undefined) {
    material.blending = THREE.NoBlending
    material.needsUpdate = true
    return
  }
  if (typeof state === 'object' && (state as { mode?: unknown }).mode === 'add') {
    material.blending = THREE.AdditiveBlending
    material.needsUpdate = true
    return
  }
  if (typeof state === 'number') {
    material.blending = state as THREE.Blending
    material.needsUpdate = true
    return
  }
  throw new Error('WebGL GSplat processing blendState 不支持')
}

function applyMaterialBlendMode(
  material: THREE.RawShaderMaterial,
  mode: GSplatMaterialBlendMode
): void {
  if (mode === 'none') {
    material.transparent = false
    material.premultipliedAlpha = false
    material.blending = THREE.NoBlending
    return
  }

  material.transparent = true
  material.premultipliedAlpha = true
  material.blending = mode === 'additive' ? THREE.AdditiveBlending : THREE.NormalBlending
}

function readWebGLLimits(renderer: THREE.WebGLRenderer): GSplatGraphicsLimits {
  const gl = safeGetContext(renderer)
  const maxColorAttachments = safeGetParameter(gl, gl?.MAX_DRAW_BUFFERS, 4)
  return Object.freeze({
    maxTextureDimension2D: Math.max(1, renderer.capabilities?.maxTextureSize ?? 4096),
    maxBufferSize: 0x7fffffff,
    maxStorageBufferBindingSize: 0,
    maxStorageBuffersPerShaderStage: 0,
    maxStorageTexturesPerShaderStage: 0,
    maxComputeWorkgroupStorageSize: 0,
    maxComputeInvocationsPerWorkgroup: 0,
    maxComputeWorkgroupSizeX: 0,
    maxComputeWorkgroupsPerDimension: 0,
    maxColorAttachments: Math.max(1, maxColorAttachments),
    // WebGL exposes per-attachment renderability rather than WebGPU's aggregate byte limit.
    maxColorAttachmentBytesPerSample: 64,
  })
}

function safeGetContext(renderer: THREE.WebGLRenderer): WebGL2RenderingContext | null {
  try {
    return renderer.getContext() as WebGL2RenderingContext
  } catch {
    return null
  }
}

function safeGetParameter(
  gl: WebGL2RenderingContext | null,
  parameter: number | undefined,
  fallback: number
): number {
  if (!gl || parameter === undefined) return fallback
  try {
    const value = gl.getParameter(parameter)
    return Number.isFinite(value) ? Number(value) : fallback
  } catch {
    return fallback
  }
}
